import { useEffect, useRef, useState, useMemo } from "react";
import GetAPI from "../../APICalls/GetQuery";
import PostAPI from "../../APICalls/PostAPI";
import PutAPI from "../../APICalls/PutAPI";
import DeleteAPI from "../../APICalls/DeleteAPI";

const KITCHEN_STORAGE_KEY = "qsr_active_kitchen";
const CLIENT_STORAGE_KEY = "qsr_active_client";

export const useQsrController = () => {
  /** ---------------- STATE ---------------- */
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState(null);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [menus, setMenus] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);

  const [activeOrderTab, setActiveOrderTab] = useState("ACTIVE");
  const [activeDashboardTab, setActiveDashboardTab] = useState("orders");
  const [loading, setLoading] = useState(true);
  const [pendingOrderActions, setPendingOrderActions] = useState({});
  const [orderActionErrors, setOrderActionErrors] = useState({});
  const [gstConfig, setGstConfig] = useState(null); // { gst, sgst, cgst }

  /** ---------------- REFS ---------------- */
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const selectedClientRef = useRef(null);
  const ordersRequestRef = useRef(0);
  const inFlightOrderIDsRef = useRef(new Set());
  const orderRefreshTimeoutRef = useRef(null);
  const onNewOrderRef = useRef(null);

  useEffect(() => {
    selectedClientRef.current = selectedClient;
  }, [selectedClient]);

  /** ---------------- ENV / APIS ---------------- */
  const ENV = process.env.REACT_APP_NODE_ENV?.toUpperCase();

  const APIS = {
    ALL_KITCHENS:
      ENV === "PRODUCTION"
        ? process.env.REACT_APP_PROD_GET_OCTOPUS_KITCHENS_API
        : process.env.REACT_APP_DEV_GET_OCTOPUS_KITCHENS_API,

    CLIENTS:
      ENV === "PRODUCTION"
        ? process.env.REACT_APP_PROD_QSR_KITCHEN_API
        : process.env.REACT_APP_DEV_QSR_KITCHEN_API,

    MENUS:
      ENV === "PRODUCTION"
        ? process.env.REACT_APP_PROD_QSR_MENUS_API
        : process.env.REACT_APP_DEV_QSR_MENUS_API,

    ORDERS:
      ENV === "PRODUCTION"
        ? process.env.REACT_APP_PROD_QSR_ORDERS_API
        : process.env.REACT_APP_DEV_QSR_ORDERS_API,

    UTILITY:
      ENV === "PRODUCTION"
        ? process.env.REACT_APP_PROD_NXTMEAL_UTILITY
        : process.env.REACT_APP_DEV_NXTMEAL_UTILITY,
  };

  const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true";
    return Boolean(value);
  };

  const normalizeMenu = (menu = {}) => ({
    ...menu,
    soldOut: toBoolean(menu.soldOut),
    orderedCount: Number(menu.orderedCount || 0),
    totalCapacity: Number(menu.totalCapacity || 0),
    bufferCount: Number(menu.bufferCount || 0),
  });

  /** ---------------- INIT ---------------- */
  useEffect(() => {
    loadAllKitchens();
    loadCategories();
  }, []);

  /** ---------------- PERSIST KITCHEN ---------------- */
  useEffect(() => {
    if (selectedKitchen) {
      localStorage.setItem(
        KITCHEN_STORAGE_KEY,
        JSON.stringify(selectedKitchen)
      );
    }
  }, [selectedKitchen]);

  useEffect(() => {
    // Kitchen changed → everything below is invalid
    setClients([]);
    setSelectedClient(null);
    setMenus([]);
    setOrders([]);
    setPendingOrderActions({});
    setOrderActionErrors({});
    inFlightOrderIDsRef.current.clear();
    ordersRequestRef.current += 1;
    cleanupWS();
  }, [selectedKitchen?.kitchenID]);


  /** ---------------- LOAD KITCHENS ---------------- */
  const loadAllKitchens = async () => {
    setLoading(true);
    try {
      const res = await GetAPI(APIS.ALL_KITCHENS, { type: "ALL_KITCHENS" });
      const list = res?.data?.body || [];
      setKitchens(list);

      const stored = JSON.parse(
        localStorage.getItem(KITCHEN_STORAGE_KEY) || "null"
      );

      const finalKitchen =
        list.find((k) => k.kitchenID === stored?.kitchenID) ||
        list[0] ||
        null;

      setSelectedKitchen(finalKitchen);
    } finally {
      setLoading(false);
    }
  };

  /** ---------------- LOAD CLIENTS (BY KITCHEN) ---------------- */
  useEffect(() => {
    if (!selectedKitchen?.kitchenID) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await GetAPI(APIS.CLIENTS, {
          kitchenID: selectedKitchen.kitchenID,
          type: "ALL_COUNTERS",
        });

        if (cancelled) return;

        const list = res?.data?.body || [];
        setClients(list);

        const stored = JSON.parse(
          localStorage.getItem(CLIENT_STORAGE_KEY) || "null"
        );

        const restoredClient =
          list.find(
            (c) =>
              c.clientID === stored?.clientID &&
              stored?.kitchenID === selectedKitchen.kitchenID
          ) ||
          list[0] ||
          null;

        setSelectedClient(restoredClient);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => (cancelled = true);
  }, [selectedKitchen?.kitchenID]);

  /** ---------------- FETCH GST CONFIG ---------------- */
  useEffect(() => {
    const fetchGstConfig = async () => {
      try {
        const res = await GetAPI(APIS.UTILITY, { type: "GST_CONFIG" });
        const configs = res?.data?.body || [];
        if (configs.length > 0 && configs[0]?.value) {
          const val = configs[0].value;
          setGstConfig({
            gst: Number(val.gst) || 0,
            sgst: Number(val.sgst) || 0,
            cgst: Number(val.cgst) || 0,
          });
        }
      } catch {
        // silently fail — GST config is optional
      }
    };
    fetchGstConfig();
  }, []);

  /** ---------------- PERSIST CLIENT ---------------- */
  useEffect(() => {
    if (!selectedClient || !selectedKitchen) return;
    localStorage.setItem(
      CLIENT_STORAGE_KEY,
      JSON.stringify({
        clientID: selectedClient.clientID,
        kitchenID: selectedKitchen.kitchenID,
      })
    );
  }, [selectedClient, selectedKitchen?.kitchenID]);

  /** ---------------- CLIENT TOGGLE ---------------- */
  const handleClientToggle = async () => {
    if (!selectedClient?.clientID) return;

    const updatedAvailability = !selectedClient.isAvailable;
    setLoading(true);

    try {
      const res = await PutAPI(APIS.CLIENTS, {
        clientID: selectedClient.clientID,
        isAvailable: updatedAvailability,
      });

      if (res?.data?.statusCode === 200) {
        setSelectedClient((prev) => ({
          ...prev,
          isAvailable: updatedAvailability,
        }));

        setClients((prev) =>
          prev.map((c) =>
            c.clientID === selectedClient.clientID
              ? { ...c, isAvailable: updatedAvailability }
              : c
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  /** ---------------- MENUS & ORDERS ---------------- */
  useEffect(() => {
    setPendingOrderActions({});
    setOrderActionErrors({});
    inFlightOrderIDsRef.current.clear();
    ordersRequestRef.current += 1;

    if (!selectedClient?.clientID) return;

    loadMenus();
    loadOrders();
    connectWS(selectedClient.clientID);

    return cleanupWS;
  }, [selectedClient?.clientID]);

  const loadMenus = async () => {
    if (!selectedClient?.clientID) return;
    const today = new Date();
    const formattedDate = `${today.getFullYear()}/${String(
      today.getMonth() + 1
    ).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}`;

    const params = {
      clientID: selectedClient.clientID,
      type: "ADMIN",
      ...(selectedClient?.isFixedMenu ? { date: formattedDate } : {}),
    };

    const res = await GetAPI(APIS.MENUS, params);
    const menuList = Array.isArray(res?.data?.body) ? res.data.body : [];
    setMenus(menuList.map(normalizeMenu));
  };

  const loadOrders = async () => {
    const clientID = selectedClientRef.current?.clientID;
    if (!clientID) return;

    const requestID = ++ordersRequestRef.current;
    const res = await GetAPI(APIS.ORDERS, {
      clientID,
    });

    if (requestID !== ordersRequestRef.current) return;
    setOrders(res?.data?.body || []);
  };

  /** ---------------- WEBSOCKET ---------------- */
  const connectWS = (clientID) => {
    cleanupWS();

    const ws = new WebSocket(
      `${process.env.REACT_APP_QSR_WS_URL}?clientID=${clientID}`
    );

    wsRef.current = ws;

     wsRef.current.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      const order = msg?.order;
      if (!order) return;

      const currentClient = selectedClientRef.current;
      if (!currentClient || order.clientID !== currentClient.clientID) return;

      setOrders((prev) => {
        const idx = prev.findIndex((o) => o.orderID === order.orderID);
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = order;
          return copy;
        }
        return [order, ...prev];
      });

      // Auto-print new orders
      console.log("[WS] Order received:", order.orderID, "status:", order.status, "hasCallback:", !!onNewOrderRef.current);
      if (order.status === "NEW" && onNewOrderRef.current) {
        console.log("[WS] Calling auto-print for order", order.orderID);
        onNewOrderRef.current(order);
      }
    };

    ws.onclose = () => {
      if (reconnectRef.current) return;
      reconnectRef.current = setTimeout(() => {
        reconnectRef.current = null;
        connectWS(clientID);
      }, 3000);
    };
  };

  const cleanupWS = () => {
    wsRef.current?.close();
    wsRef.current = null;
    reconnectRef.current && clearTimeout(reconnectRef.current);
    reconnectRef.current = null;
    if (orderRefreshTimeoutRef.current) {
      clearTimeout(orderRefreshTimeoutRef.current);
      orderRefreshTimeoutRef.current = null;
    }
  };

  const isApiSuccess = (res) => {
    const statusCode = res?.data?.statusCode;
    return (
      statusCode === 200 ||
      statusCode === 201
    );
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /** ---------------- FILTERED ORDERS ---------------- */
  const visibleOrders = useMemo(() => {
    const map = {
      ACTIVE: "NEW",
      PICKUP: "READY",
      DELIVERED: "DELIVERED",
      CANCELLED: "CANCELLED",
    };
    return orders
      .filter((o) => o.status === map[activeOrderTab])
      .sort((a, b) => (b.createdAtEpoch || 0) - (a.createdAtEpoch || 0));
  }, [orders, activeOrderTab]);

  /** ---------------- CATEGORIES ---------------- */
  const loadCategories = async () => {
    const res = await GetAPI(APIS.MENUS, { type: "CATEGORIES" });
    setCategories(res?.data?.body || []);
  };

  /** ---------------- MENU ACTIONS ---------------- */
  const handleToggleMenu = (menuOrID, nextSoldOut) => {
    const menu =
      typeof menuOrID === "object"
        ? menuOrID
        : menus.find((m) => m.menuID === menuOrID);

    if (!menu) return;

    const payload = menu?.sortKey
      ? {
          clientID: menu.clientID || selectedClient?.clientID,
          sortKey: menu.sortKey,
          soldOut: typeof nextSoldOut === "boolean" ? nextSoldOut : !menu.soldOut,
        }
      : {
          menuID: menu.menuID,
          soldOut: typeof nextSoldOut === "boolean" ? nextSoldOut : !menu.soldOut,
        };

    PutAPI(APIS.MENUS, payload).then((res) => {
      if (res?.data?.statusCode === 200) loadMenus();
    });
  };

  const saveMenu = async (menuData, isAdding) => {
    setLoading(true);
    try {
      const api = isAdding ? PostAPI : PutAPI;
      const res = await api(APIS.MENUS, {
        ...menuData,
        isInStock: Boolean(menuData.isInStock),
        inStock: menuData.isInStock ? Number(menuData.inStock) : 0,
        clientID: selectedClient?.clientID,
        prepTime: Number(menuData.prepTime || 0),
      });

      if (res?.data?.statusCode === 200) {
        await loadMenus();
        return true;
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteMenu = async (menu) => {
    const res = await DeleteAPI(APIS.MENUS, { menuID: menu.menuID });
    if (res?.data?.statusCode === 200) loadMenus();
  };

  const updateBufferCount = async (menu, nextBufferCount, alsoUnsoldOut = false) => {
    const clientID = menu?.clientID || selectedClient?.clientID;
    const sortKey = menu?.sortKey;
    const parsed = Number(nextBufferCount);

    if (!clientID || !sortKey || Number.isNaN(parsed) || parsed < 0) {
      return false;
    }

    const payload = {
      clientID,
      sortKey,
      bufferCount: parsed,
    };

    if (alsoUnsoldOut) {
      payload.soldOut = false;
    }

    const res = await PutAPI(APIS.MENUS, payload);

    if (res?.data?.statusCode === 200) {
      const rawBody = res?.data?.body;
      const updatedRows = Array.isArray(rawBody)
        ? rawBody
        : rawBody && typeof rawBody === "object"
          ? [rawBody]
          : [];

      if (updatedRows.length > 0) {
        const normalizedRows = updatedRows.map(normalizeMenu);

        setMenus((prev) => {
          const bySortKey = new Map(
            normalizedRows
              .filter((row) => row?.sortKey)
              .map((row) => [row.sortKey, row])
          );

          if (bySortKey.size === 0) return prev;

          return prev.map((m) =>
            bySortKey.has(m.sortKey) ? bySortKey.get(m.sortKey) : m
          );
        });
      }

      await loadMenus();
      return true;
    }

    return false;
  };

  /** ---------------- HOT SELLING ---------------- */
  const saveHotSellingItems = async (selectedMenuIDs) => {
    setLoading(true);
    try {
      const updates = menus.map((menu) => {
        const isSelected = selectedMenuIDs.includes(menu.menuID);
        const isCurrentlyHot = menu.category === "Hot Selling";
        if (isSelected && !isCurrentlyHot) {
          return PutAPI(APIS.MENUS, { menuID: menu.menuID, category: "Hot Selling" });
        }
        if (!isSelected && isCurrentlyHot) {
          return PutAPI(APIS.MENUS, { menuID: menu.menuID, category: "" });
        }
        return null;
      }).filter(Boolean);

      await Promise.all(updates);
      await loadMenus();
    } finally {
      setLoading(false);
    }
  };

  /** ---------------- ORDER ACTION ---------------- */
  const updateOrderStatus = (orderID, newStatus, cashPaymentMode) => {
    if (!orderID || !newStatus) return;
    if (inFlightOrderIDsRef.current.has(orderID)) return;
    inFlightOrderIDsRef.current.add(orderID);

    const previousOrder = orders.find((o) => o.orderID === orderID);
    const previousStatus = previousOrder?.status;
    const clientID = selectedClientRef.current?.clientID;

    setPendingOrderActions((prev) => ({ ...prev, [orderID]: true }));
    setOrderActionErrors((prev) => {
      const next = { ...prev };
      delete next[orderID];
      return next;
    });

    setOrders((prev) =>
      prev.map((order) =>
        order.orderID === orderID ? { ...order, status: newStatus } : order
      )
    );

    const syncOrder = async () => {
      try {
        let response;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          const payload = { orderID, status: newStatus };
          if (cashPaymentMode) {
            payload.CashPaymentMode = cashPaymentMode; // "ONLINE" | "CASH"
          }
          response = await PutAPI(APIS.ORDERS, payload);
          if (isApiSuccess(response)) break;
          if (attempt < 2) {
            await wait((attempt + 1) * 700);
          }
        }

        if (!isApiSuccess(response)) {
          throw new Error("ORDER_STATUS_UPDATE_FAILED");
        }

        if (clientID && clientID === selectedClientRef.current?.clientID) {
          await loadOrders();
          if (orderRefreshTimeoutRef.current) {
            clearTimeout(orderRefreshTimeoutRef.current);
          }
          orderRefreshTimeoutRef.current = setTimeout(() => {
            orderRefreshTimeoutRef.current = null;
            if (clientID === selectedClientRef.current?.clientID) {
              loadOrders();
            }
          }, 1200);
        }
      } catch (error) {
        if (previousStatus) {
          setOrders((prev) =>
            prev.map((order) =>
              order.orderID === orderID
                ? { ...order, status: previousStatus }
                : order
            )
          );
        }

        setOrderActionErrors((prev) => ({
          ...prev,
          [orderID]: "Could not update. Please retry.",
        }));
      } finally {
        inFlightOrderIDsRef.current.delete(orderID);
        setPendingOrderActions((prev) => {
          const next = { ...prev };
          delete next[orderID];
          return next;
        });
      }
    };

    syncOrder();
  };

  /** ---------------- BULK ORDER STATUS ---------------- */
  const bulkUpdateOrderStatus = async (orderIDs, newStatus) => {
    if (!orderIDs?.length || !newStatus) return;

    const clientID = selectedClientRef.current?.clientID;

    try {
      const response = await PutAPI(APIS.ORDERS, {
        orderIDs,
        type: newStatus,
      });

      if (isApiSuccess(response)) {
        if (clientID && clientID === selectedClientRef.current?.clientID) {
          await loadOrders();
        }
      }
    } catch {
      // silently fail — individual orders can be retried
    }
  };

  /** ---------------- PLACE ORDER (SITE ORDERS) ---------------- */
  const placeOrder = async ({ items, paymentStatus, cashPaymentMode, gstApplicable, gstAmount, gstPercent, itemTotal }) => {
    const client = selectedClientRef.current;
    if (!client?.clientID || !items?.length) return false;

    const storedUser = localStorage.getItem("userName") || "Site Manager";
    const craverName = `${storedUser} - site manager`;

    const today = new Date();
    const deliveryDate = `${today.getFullYear()}/${String(
      today.getMonth() + 1
    ).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}`;

    const totalAmount = items.reduce(
      (sum, item) => sum + (Number(item.itemPrice) || 0) * (Number(item.quantity) || 0),
      0
    );

    const kitchen = selectedKitchen;

    // Compute final payable amount based on GST
    const isCash = cashPaymentMode.toUpperCase() === "CASH";
    let payableAmount = totalAmount;
    if (gstApplicable && gstPercent > 0) {
      payableAmount = isCash
        ? totalAmount + Math.ceil(gstAmount)
        : parseFloat((totalAmount + gstAmount).toFixed(2));
    }

    const payload = {
      clientID: client.clientID,
      clientName: client.clientName,
      locationName: client.locationName,
      locationID: client.locationID || "",
      subLocationName: client.subLocationName,
      subLocationID: client.subLocationID || "",
      kitchenID: kitchen?.kitchenID || client.kitchenID || "",
      kitchenName: kitchen?.kitchenName || "",
      orderSource: "website",
      orderType: "QSC",
      craverName,
      orderedItems: items.map((i) => ({
        category: i.category || "",
        imageUrl: i.imageUrl || "https://d2nahbmqd5vvug.cloudfront.net/appdata/icon_foodLove.png",
        inStock: Number(i.inStock || 0),
        isInStock: Boolean(i.isInStock),
        itemDescription: i.itemDescription || i.description || "",
        itemName: i.itemName || i.menuName || "",
        itemType: i.itemType || "Vegetarian",
        menuID: i.menuID,
        menuType: i.menuType || "",
        ...(i.sortKey ? { sortKey: i.sortKey } : {}),
        pax: { "1": Number(i.quantity) },
        quantity: Number(i.quantity),
        soldOut: Boolean(i.soldOut),
        splitMenu: i.splitMenu || {},
        totalAmount: (Number(i.itemPrice) || 0) * Number(i.quantity),
      })),
      deliveryDate,
      paymentStatus,
      status: "NEW",
      totalAmount: payableAmount,
      payableAmount,
      ...(items[0]?.menuType ? { menuType: items[0].menuType } : {}),
      gstApplicable: Boolean(gstApplicable),
      ...(gstPercent > 0 && {
        gstAmount: isCash ? Math.ceil(gstAmount) : gstAmount,
        gstPercent,
        itemTotal,
      }),
      CashPaymentMode: cashPaymentMode,
    };

    try {
      const res = await PostAPI(APIS.ORDERS, payload);
      if (isApiSuccess(res)) {
        await loadOrders();
        return { success: true };
      }
      // Check for 409 or other error responses
      const statusCode = res?.response?.status || res?.response?.data?.statusCode || res?.data?.statusCode;
      const errorBody = res?.response?.data || res?.data;
      const errMsg = typeof errorBody?.body === "string" ? errorBody.body
        : errorBody?.body?.message || errorBody?.message || "Order failed. Please try again.";
      if (statusCode === 409) {
        return { success: false, status: 409, message: errMsg };
      }
      return { success: false, message: errMsg };
    } catch (err) {
      const statusCode = err?.response?.status || err?.response?.data?.statusCode;
      const errorBody = err?.response?.data;
      const errMsg = typeof errorBody?.body === "string" ? errorBody.body
        : errorBody?.body?.message || errorBody?.message || "Order failed. Please try again.";
      if (statusCode === 409) {
        return { success: false, status: 409, message: errMsg };
      }
      return { success: false, message: errMsg };
    }
  };

  const openLiveCounter = () => {
    if (!selectedClient?.clientID) return;
    window.open(
      `${window.location.origin}/live/${selectedClient.clientID}`,
      "_blank"
    );
  };

  /** ---------------- RETURN ---------------- */
  return {
    loading,
    kitchens,
    selectedKitchen,
    setSelectedKitchen,
    clients,
    selectedClient,
    setSelectedClient,
    handleClientToggle,
    menus,
    orders: visibleOrders,
    categories,
    activeDashboardTab,
    setActiveDashboardTab,
    activeOrderTab,
    setActiveOrderTab,
    reloadMenus: loadMenus,
    reloadOrders: loadOrders,
    handleToggleMenu,
    saveMenu,
    deleteMenu,
    updateBufferCount,
    saveHotSellingItems,
    updateOrderStatus,
    bulkUpdateOrderStatus,
    placeOrder,
    pendingOrderActions,
    orderActionErrors,
    openLiveCounter,
    gstConfig,
    onNewOrderRef,
  };
};
